import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, Globe, Package, Shield, Clock, TrendingUp, Users, MapPin, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const heroSlides = [
    {
      image: 'https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg',
      title: 'Premium Makhana Exports',
      subtitle: 'Finest quality fox nuts from Bihar'
    },
    {
      image: 'https://images.pexels.com/photos/18328392/pexels-photo-18328392.jpeg',
      title: 'Basmati Rice Excellence',
      subtitle: 'Aromatic long-grain perfection'
    },
    {
      image: 'https://images.unsplash.com/photo-1675654871683-abf6524f68c6',
      title: 'Authentic Indian Spices',
      subtitle: 'Rich flavors from source to destination'
    },
    {
      image: 'https://images.unsplash.com/photo-1764361276489-79b17d9a8782',
      title: 'Premium Coffee Beans',
      subtitle: 'Arabica & Robusta from South India'
    }
  ];

  const products = [
    { name: 'Makhana', slug: 'makhana', image: 'https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg' },
    { name: 'Basmati Rice', slug: 'basmati-rice', image: 'https://images.pexels.com/photos/18328392/pexels-photo-18328392.jpeg' },
    { name: 'Non-Basmati Rice', slug: 'non-basmati-rice', image: 'https://images.pexels.com/photos/15879424/pexels-photo-15879424.jpeg' },
    { name: 'Dehydrated Onion', slug: 'dehydrated-onion-powder', image: 'https://images.unsplash.com/photo-1606172150255-64e15da65c8c' },
    { name: 'Vegetables', slug: 'vegetables', image: 'https://images.unsplash.com/photo-1558458655-6c6f8ee827ea' },
    { name: 'Fresh Fruits', slug: 'fresh-fruits', image: 'https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg' },
    { name: 'Spices', slug: 'spices', image: 'https://images.unsplash.com/photo-1675654871683-abf6524f68c6' },
    { name: 'Pulses', slug: 'pulses', image: 'https://images.pexels.com/photos/4750274/pexels-photo-4750274.jpeg' },
    { name: 'Coffee Beans', slug: 'coffee-beans', image: 'https://images.unsplash.com/photo-1764361276489-79b17d9a8782' },
  ];

  const strengths = [
    { icon: <Package className="w-8 h-8" />, title: 'Structured Procurement Model', description: 'Systematic sourcing from verified suppliers across India' },
    { icon: <Shield className="w-8 h-8" />, title: 'Quality Inspection & Compliance', description: 'Rigorous quality checks at every stage of procurement' },
    { icon: <Globe className="w-8 h-8" />, title: 'Transparent Communication', description: 'Real-time updates and clear documentation throughout' },
    { icon: <Clock className="w-8 h-8" />, title: 'Real-time Shipment Updates', description: 'Track your orders from warehouse to destination' },
    { icon: <TrendingUp className="w-8 h-8" />, title: 'Competitive Pricing', description: 'Best market rates with no compromise on quality' },
    { icon: <Users className="w-8 h-8" />, title: 'Long-term Partnership Approach', description: 'Building relationships that span years, not transactions' },
  ];

  const plugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false })
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section - Text Left, Carousel Right */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#1e3a5f] via-[#2c5f8d] to-[#1e3a5f]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left Text-Heavy Section */}
            <div className="lg:col-span-3 text-white">
              <div className="inline-block mb-4 px-4 py-1.5 bg-[#c9a961]/20 border border-[#c9a961]">
                <span className="text-[#e6d5a8] text-xs font-semibold tracking-widest uppercase">Trusted Since 1999</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-8 leading-tight" data-testid="hero-title">
                Reliable Global Sourcing & Export Solutions from India
              </h1>

              <div className="space-y-4 mb-10 text-lg text-slate-200 leading-relaxed">
                <p className="text-xl">
                  <strong className="text-white font-semibold">Structured procurement.</strong> Quality-driven supply. Transparent execution.
                </p>
                <p>
                  Partner with Vista Global Trade for premium agricultural exports backed by 25+ years of expertise, rigorous quality control, and seamless export coordination to 50+ countries worldwide.
                </p>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-2 gap-4 mb-10 pb-10 border-b border-white/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#c9a961] flex-shrink-0" />
                  <span className="text-sm">APEDA Certified</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#c9a961] flex-shrink-0" />
                  <span className="text-sm">ISO Compliant</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#c9a961] flex-shrink-0" />
                  <span className="text-sm">Real-time Tracking</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#c9a961] flex-shrink-0" />
                  <span className="text-sm">Lab Tested Products</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  data-testid="explore-products-button"
                  className="bg-[#c9a961] text-[#1e3a5f] hover:bg-[#e6d5a8] px-10 py-4 font-bold transition-all duration-300 text-sm tracking-wider uppercase shadow-2xl hover:shadow-xl hover:-translate-y-1 text-center"
                >
                  Explore Products
                </Link>
                <Link
                  to="/contact"
                  data-testid="get-in-touch-button"
                  className="bg-transparent border-2 border-[#c9a961] text-[#e6d5a8] hover:bg-[#c9a961] hover:text-[#1e3a5f] px-10 py-4 font-bold transition-all duration-300 text-sm tracking-wider uppercase text-center"
                >
                  Get in Touch
                </Link>
              </div>
            </div>

            {/* Right Carousel Section */}
            <div className="lg:col-span-2">
              <Carousel
                plugins={[plugin.current]}
                className="w-full"
                opts={{
                  loop: true,
                }}
              >
                <CarouselContent>
                  {heroSlides.map((slide, index) => (
                    <CarouselItem key={index}>
                      <div className="relative">
                        <div className="relative h-[550px] overflow-hidden border-4 border-[#c9a961]/30">
                          <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/90 via-[#1e3a5f]/40 to-transparent"></div>
                          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                            <div className="inline-block mb-3 px-3 py-1 bg-[#c9a961] text-[#1e3a5f] text-xs font-bold tracking-widest uppercase">
                              Featured Product
                            </div>
                            <h3 className="text-3xl font-light mb-2">{slide.title}</h3>
                            <p className="text-sm text-slate-200">{slide.subtitle}</p>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* Single Stat Below Carousel */}
              <div className="mt-8 bg-white/10 backdrop-blur-sm border-l-4 border-[#c9a961] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-4xl font-light text-[#c9a961] mb-2">25+</div>
                    <div className="text-sm text-slate-200 uppercase tracking-wider">Years of Excellence</div>
                  </div>
                  <CheckCircle className="w-12 h-12 text-[#c9a961]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-28 md:py-36 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block mb-6">
                <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase border-b-2 border-[#c9a961] pb-1">About Vista</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1e3a5f] mb-8">
                Building Global Trade Partnerships
              </h2>
              <div className="space-y-6 text-slate-600 leading-relaxed text-lg">
                <p>
                  With <strong className="text-[#2c5f8d]">25+ years of product experience</strong>, Vista Global Trade brings deep understanding of agricultural commodities and structured procurement processes.
                </p>
                <p>
                  We leverage a <strong className="text-[#2c5f8d]">strong procurement network</strong> across India to source premium quality products that meet international standards.
                </p>
                <p>
                  Our <strong className="text-[#2c5f8d]">quality-focused approach</strong> ensures every batch undergoes rigorous inspection before export.
                </p>
                <p>
                  We provide comprehensive <strong className="text-[#2c5f8d]">export-ready documentation support</strong>, making international trade seamless for our partners.
                </p>
              </div>
              <Link
                to="/about"
                data-testid="learn-more-button"
                className="inline-flex items-center gap-3 mt-10 text-[#2c5f8d] font-semibold text-lg hover:gap-5 transition-all duration-300 group"
              >
                Learn More
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#c9a961]/10 -z-10"></div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#2c5f8d]/10 -z-10"></div>
              <img
                src="https://images.pexels.com/photos/6950015/pexels-photo-6950015.jpeg"
                alt="Global Trade"
                className="w-full h-[550px] object-cover shadow-2xl relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Strengths Section */}
      <section className="py-28 md:py-36 bg-[#1e3a5f]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase">Our Expertise</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-white mb-6 mt-4">
              Why Choose Vista Global Trade
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Our commitment to excellence is reflected in every aspect of our operations
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strengths.map((strength, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm p-8 border-l-4 border-[#c9a961] hover:bg-white/10 hover:border-l-8 transition-all duration-300 group"
                data-testid={`strength-card-${index}`}
              >
                <div className="text-[#c9a961] mb-4 group-hover:scale-110 transition-transform">{strength.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{strength.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{strength.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Grid */}
      <section className="py-28 md:py-36 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase">What We Export</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1e3a5f] mb-6 mt-4">
              Our Product Range
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Premium quality exports across diverse categories
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Link
                key={product.slug}
                to={`/products/${product.slug}`}
                data-testid={`product-card-${product.slug}`}
                className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-[#c9a961] hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/90 via-[#1e3a5f]/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-semibold text-white mb-2">
                      {product.name}
                    </h3>
                    <span className="inline-flex items-center gap-2 text-[#c9a961] font-medium group-hover:gap-4 transition-all duration-300">
                      View Details <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quality & Compliance Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/14020705/pexels-photo-14020705.jpeg"
                alt="Quality Control"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0B1B32] mb-6">
                Quality & Compliance
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Vendor Verification</h3>
                    <p className="text-slate-600 text-sm">Thorough assessment of supplier credentials and capabilities</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Batch Inspection</h3>
                    <p className="text-slate-600 text-sm">Sample testing for every procurement batch</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Moisture & Quality Parameters</h3>
                    <p className="text-slate-600 text-sm">Adherence to international quality standards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Export-grade Packaging</h3>
                    <p className="text-slate-600 text-sm">Proper packaging to maintain product integrity during transit</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Documentation Checks</h3>
                    <p className="text-slate-600 text-sm">Complete compliance with export requirements</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-28 md:py-36 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-6 md:px-12 max-w-7xl">
            <div className="text-center mb-16">
              <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase">Client Testimonials</span>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1e3a5f] mb-6 mt-4">
                What Our Partners Say
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Trusted by businesses across the globe
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Array.isArray(testimonials) && testimonials.slice(0, 4).map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white p-10 border-l-4 border-[#c9a961] shadow-lg hover:shadow-2xl transition-all duration-300"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#c9a961] text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-slate-700 text-lg leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t border-slate-200 pt-6">
                    <p className="font-semibold text-[#1e3a5f] text-lg">{testimonial.name}</p>
                    <p className="text-sm text-slate-600 mt-1">{testimonial.company}</p>
                    <p className="text-sm text-[#c9a961] font-medium mt-1">{testimonial.country}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-28 md:py-36 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Contact Info */}
            <div>
              <div className="inline-block mb-6">
                <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase border-b-2 border-[#c9a961] pb-1">Get in Touch</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1e3a5f] mb-8">
                Ready to Start Your Export Journey?
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-10">
                Connect with our team to discuss your sourcing requirements. We're here to provide detailed product specifications, pricing, and export documentation support.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#2c5f8d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Our Office</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      313, U.S Complex, Jasola Vihar,<br />
                      Delhi-110076, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#2c5f8d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Email Us</h3>
                    <a href="mailto:info@vistaglobaltrade.com" className="text-slate-600 hover:text-[#2c5f8d] transition-colors text-sm">
                      info@vistaglobaltrade.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#2c5f8d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Call Us</h3>
                    <a href="tel:+918882062043" className="text-slate-600 hover:text-[#2c5f8d] transition-colors text-sm">
                      +91 8882062043
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-[#F8FAFC] border-l-4 border-[#c9a961]">
                <h3 className="font-semibold text-[#1e3a5f] mb-2">Business Hours</h3>
                <p className="text-slate-600 text-sm">Monday - Saturday: 9:00 AM - 6:00 PM IST</p>
                <p className="text-slate-600 text-sm">Sunday: Closed</p>
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="bg-[#F8FAFC] p-8 md:p-10">
              <h3 className="text-2xl font-semibold text-[#1e3a5f] mb-6">Send Us a Message</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = {
                  name: formData.get('name'),
                  email: formData.get('email'),
                  phone: formData.get('phone') || '',
                  product: formData.get('product') || '',
                  message: formData.get('message')
                };

                // Validate required fields
                if (!data.name || !data.email || !data.message) {
                  toast.error('Please fill in all required fields');
                  return;
                }

                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(data.email)) {
                  toast.error('Please enter a valid email address');
                  return;
                }

                try {
                  // Backend handles both database save and WhatsApp notification
                  await axios.post(`${API}/contact`, data);
                  toast.success('Message sent successfully! We will contact you soon.');
                  e.target.reset();
                } catch (error) {
                  console.error('Error:', error);
                  toast.error('Failed to send message. Please try again or contact us via WhatsApp.');
                }
              }} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#2c5f8d] focus:ring-1 focus:ring-[#2c5f8d] outline-none transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:border-[#2c5f8d] focus:ring-1 focus:ring-[#2c5f8d] outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-3 border border-slate-300 focus:border-[#2c5f8d] focus:ring-1 focus:ring-[#2c5f8d] outline-none transition-colors"
                      placeholder="+91 xxxxx xxxxx"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Product Interest</label>
                  <input
                    type="text"
                    name="product"
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#2c5f8d] focus:ring-1 focus:ring-[#2c5f8d] outline-none transition-colors"
                    placeholder="e.g., Basmati Rice, Makhana"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    className="w-full px-4 py-3 border border-slate-300 focus:border-[#2c5f8d] focus:ring-1 focus:ring-[#2c5f8d] outline-none transition-colors resize-none"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#2c5f8d] text-white hover:bg-[#1e3a5f] py-4 font-semibold transition-all duration-300 text-sm tracking-wider uppercase shadow-lg hover:shadow-xl"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 bg-gradient-to-br from-[#1e3a5f] via-[#2c5f8d] to-[#1e3a5f] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#c9a961] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#c9a961] rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-6 md:px-12 max-w-7xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-8">
            Looking for a Reliable Indian Supply Partner?
          </h2>
          <p className="text-slate-200 text-xl mb-12 max-w-3xl mx-auto font-light">
            Let's discuss how we can support your sourcing requirements with premium quality products and transparent execution
          </p>
          <Link
            to="/contact"
            data-testid="request-quotation-button"
            className="inline-block bg-[#c9a961] text-[#1e3a5f] hover:bg-[#e6d5a8] px-12 py-5 font-bold transition-all duration-300 text-sm tracking-widest uppercase shadow-2xl hover:shadow-xl hover:-translate-y-1"
          >
            Request Quotation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;